import React, { Suspense } from 'react';
import { observer } from 'mobx-react';
import paymentStore from 'Models/PaymentStore';
import { PaymentMethodId } from 'Views/Components/_HumanWritten/Payment/PaymentTypeWrapper';
import { PAYMENT_REQUEST_TIMEOUT_MS } from 'Services/Api/_HumanWritten/PaymentService';
import { isNullOrUndefined } from 'Util/TypeGuards';
import { TripType } from '../BookingWizard/FerryTripBookingWizard/BookingWizardTypes';
import { LottieSpinner } from '../Lottie/LottieSpinner';
import CheckOutForm from './CheckOutForm';
import { CardFieldsState, CardFieldsStateProvider } from './PaymentUnavailable';
import {
	clearSimulationDecisions,
	consumeStallSimulation,
	isStallSimulationArmed,
	shouldSimulatePaymentFailure,
} from './paymentFailureSimulation';

/**
 * Lazy-loaded provider SDK chunks. The active provider's chunk is fetched only when needed,
 * so Stripe-only operators never download Square code and vice versa.
 */
const StripeProvider = React.lazy(() => import('./StripeProvider'));
const SquareProvider = React.lazy(() => import('./SquareProvider'));

export interface PaymentProviderWrapperProps {
	transactionId: string;
	userId?: string;
	isAlteration: boolean;
	tripType?: TripType;
	eventBooking: boolean;
	redirectUrl?: string;
	includeSubmitButtons?: boolean;
	/** Undefined until the fetched amount has been synced into the payment context. */
	totalPrice?: number;
	paymentType?: PaymentMethodId;
	onCancelRedirectUrl?: string;
	inCheckIn: boolean;
	/**
	 * Load state of the amount-payable fetch. Without it a failed fetch and a not-yet-returned one
	 * both arrive here as `totalPrice` 0, which used to render as an endless spinner.
	 */
	amountStatus?: 'loading' | 'error' | 'data';
	/** Re-runs the amount-payable fetch. */
	onRetryAmount?: () => void;
	/**
	 * Whether the FETCHED amount is genuinely zero.
	 *
	 * Deliberately not inferred from `totalPrice === 0` here. `totalPrice` reaches this component
	 * through the payment context, which is written in an effect — so for one paint after the fetch
	 * resolves, `amountStatus` is already `data` while `totalPrice` is still unset. Reading a zero
	 * out of that gap flashed "Nothing to pay" on every normal payment load.
	 */
	amountIsZero?: boolean;
}

/**
 * How long the form may sit in a legitimate loading state before we stop calling it loading. Every
 * input here is a network round trip that can hang without rejecting, so a spinner with no upper
 * bound is a spinner that can run for the life of the page — which is exactly what customers hit.
 *
 * Deliberately LONGER than the per-request timeout rather than equal to it. Each input this waits on
 * is bounded by `PAYMENT_REQUEST_TIMEOUT_MS`, so at the same value the two fire together and which
 * message the customer gets — "could not be loaded" or "taking too long" — comes down to which
 * callback React happens to process first. This is the outer bound of last resort, for a wait that
 * nothing else rejects; it only has a job to do once the inner timeouts have had their chance.
 */
const STALL_TIMEOUT_MS = PAYMENT_REQUEST_TIMEOUT_MS + 15_000;

/**
 * Provider-aware wrapper that loads the active payment provider's SDK and renders the
 * checkout form inside it. Branches on <c>paymentStore.provider</c>; new providers slot in
 * via additional <c>React.lazy</c> imports + branches.
 *
 * Replaces the old Stripe-only <c>CheckOutFormWrapper</c>.
 */
function PaymentProviderWrapper({
	totalPrice,
	amountStatus = 'data',
	onRetryAmount,
	amountIsZero = false,
}: PaymentProviderWrapperProps) {
	// Payment config is no longer loaded at app startup; load it on demand the first time any
	// payment UI mounts. retrieveAsync is idempotent (guards on its own ready/fetched flags).
	React.useEffect(() => {
		if (!paymentStore.isReady) {
			paymentStore.retrieveAsync();
		}
	}, []);

	// Armed from sessionStorage — see paymentFailureSimulation. One-shot, so retry recovers. Read on
	// every render rather than captured at mount: the module caches the decision, so this is stable
	// across re-renders and across StrictMode's double mount, and a retry resets it there.
	const simulated = {
		config: shouldSimulatePaymentFailure('config'),
		amount: shouldSimulatePaymentFailure('amount'),
		zero: shouldSimulatePaymentFailure('zero'),
	};
	const simulatingStall = isStallSimulationArmed();

	// The last amount the provider chunk was mountable with, and — being non-null — the record that
	// the chunk HAS been mounted.
	//
	// Latched on purpose. What the card slot shows must never also decide whether the provider chunk
	// is in the tree: the two branches at the bottom of this component render different element types
	// at the same position, so flipping between them unmounts <Elements> and destroys the mounted
	// Payment Element. That is the remount StripeProvider's "No `key` here on purpose" note exists to
	// prevent, and it is reachable from the ordinary surcharge flow — a SurchargeChangedException
	// makes StripeCheckoutForm call handleSurchargeUpdate(true), which toggles `triggerRecalculation`,
	// which puts useAsync back into 'loading'. The card details the customer had already typed went
	// with it, the retry after the surcharge modal found no mounted element, and CheckOutForm's
	// in-flight terminal-payment poll was torn down alongside.
	//
	// So: wait for the first real amount, then keep the chunk mounted for the life of this component
	// and report everything later through context instead.
	//
	// `simulatingStall` is in the guard so the dev stall simulation still has something to simulate;
	// it is always false in a production build.
	const mountedAmount = React.useRef<number | null>(null);
	if (paymentStore.isReady && !isNullOrUndefined(totalPrice) && !simulatingStall) {
		mountedAmount.current = totalPrice;
	}

	// Nothing below rejects on its own if a request simply never answers, so bound the wait. After
	// this the form stops claiming to be loading and offers a way out.
	//
	// `ready` has to name EVERY input the loading branch below tests, `totalPrice` included. It used
	// to stop at `isReady && amountStatus === 'data'`, which left the `isNullOrUndefined(totalPrice)`
	// branch — reachable on its own, because `amountStatus` defaults to 'data' and `totalPrice` is
	// optional — rendering a spinner with no timer behind it. That is the exact failure this whole
	// component exists to remove, so the bound has to cover the same set of conditions the branch does.
	const [stalled, setStalled] = React.useState(false);
	// Bumped by a retry. Without it the effect has no dependency that a retry changes, so pressing
	// Try again cleared `stalled` and then armed nothing — the second attempt had no stall bound at
	// all, and the spinner came back permanently if it also hung.
	const [attempt, setAttempt] = React.useState(0);
	React.useEffect(() => {
		setStalled(false);
		// Only the FIRST load is bounded here. Once the provider chunk is mounted there is a working
		// form on screen, and replacing it with "taking too long" would destroy the card fields the
		// customer is typing into — the very thing the latch above exists to stop. A re-fetch is
		// already bounded by PAYMENT_REQUEST_TIMEOUT_MS, which surfaces as the `amount` failure with
		// its own Try again.
		const ready =
			mountedAmount.current !== null
			|| (paymentStore.isReady && amountStatus === 'data' && !isNullOrUndefined(totalPrice));
		if (ready && !simulatingStall) {
			return undefined;
		}
		const timer = setTimeout(() => {
			if (simulatingStall) {
				consumeStallSimulation();
			}
			setStalled(true);
		}, simulatingStall ? 3_000 : STALL_TIMEOUT_MS);
		return () => clearTimeout(timer);
	}, [paymentStore.isReady, amountStatus, totalPrice, simulatingStall, attempt]);

	const retryConfig = React.useCallback(() => {
		setStalled(false);
		setAttempt(current => current + 1);
		// Clear any simulated failure so Try again visibly recovers rather than re-showing the same
		// state — the storage key was already disarmed, so re-evaluating now returns false.
		clearSimulationDecisions();
		paymentStore.retrieveAsync(true);
		onRetryAmount?.();
	}, [onRetryAmount]);

	// Whatever is wrong, CheckOutForm still renders — it owns `<form id={PAYMENT_FORM_ID}>`, which
	// cash, EFTPOS, invoice and pay-later all submit through. Only the card slot inside it changes.
	// Returning an error in place of the form is what used to take every other payment method down
	// with the card fields, on the very screen offering them as the way out.
	const cardFieldsState: CardFieldsState | null = (() => {
		if (paymentStore.hasError || simulated.config) {
			return { kind: 'unavailable', reason: 'config', onRetry: retryConfig };
		}

		if (amountStatus === 'error' || simulated.amount) {
			return { kind: 'unavailable', reason: 'amount', onRetry: retryConfig };
		}

		// Checked before the loading branch, not inside it: once the stall has been declared the
		// inputs may well have arrived, and falling straight through to the card fields would flash
		// the error away before it could be read.
		if (stalled) {
			return { kind: 'unavailable', reason: 'stalled', onRetry: retryConfig };
		}

		// `totalPrice` undefined is the sync gap described on the prop: the fetch has landed but the
		// context write has not. Still loading as far as this component is concerned, and bounded by
		// the stall timer above so it cannot become another indefinite spinner.
		if (
			!paymentStore.isReady
			|| amountStatus === 'loading'
			|| simulatingStall
			|| isNullOrUndefined(totalPrice)
		) {
			// Before the provider chunk mounts, this is the first load and the slot shows a spinner.
			// After it, the only thing that can still be loading is a RE-fetch of the amount — and the
			// card fields have to stay exactly where they are through one. Hiding them unmounts the
			// Payment Element the customer has already typed into and is part-way through submitting,
			// which is the same damage as unmounting the provider, one level further down. The
			// surcharge modal that triggered the re-fetch is already saying what is happening.
			return mountedAmount.current === null ? { kind: 'loading' } : null;
		}

		// A zero amount is a value, not a loading state. It means the transaction is not payable,
		// which should have been settled before the customer ever reached a payment form — treating
		// it as "still loading" is what left them watching a spinner that could never resolve.
		if (amountIsZero || simulated.zero) {
			return { kind: 'unavailable', reason: 'zero' };
		}

		return null;
	})();

	// Nothing to mount a provider chunk FOR yet — the config has not arrived, so we do not even know
	// which provider it would be. The form still mounts so cash, EFTPOS, invoice and pay-later work;
	// the card slot shows the reason.
	if (mountedAmount.current === null) {
		return (
			<CardFieldsStateProvider value={cardFieldsState ?? { kind: 'loading' }}>
				<CheckOutForm />
			</CardFieldsStateProvider>
		);
	}

	const provider = paymentStore.provider;

	if (provider === 'STRIPE') {
		return (
			<Suspense fallback={<LottieSpinner />}>
				<StripeProvider totalPrice={mountedAmount.current} cardFieldsState={cardFieldsState}>
					<CheckOutForm />
				</StripeProvider>
			</Suspense>
		);
	}

	if (provider === 'SQUARE') {
		return (
			<Suspense fallback={<LottieSpinner />}>
				<SquareProvider totalPrice={mountedAmount.current} cardFieldsState={cardFieldsState}>
					<CheckOutForm />
				</SquareProvider>
			</Suspense>
		);
	}

	return <div className="payment-provider-error">Unsupported payment provider: {provider}</div>;
}

export default observer(PaymentProviderWrapper);
